import React from "react";
import {
  Grid,
  Typography,
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder";
import { useTranslation } from "react-i18next";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const WeekSummary = ({ data, currencyCode }) => {
  const { t, i18n } = useTranslation();
  const isLoading = !data;
  const weekSummaryOrders = data?.[0]?.sparkline;
  const weekSummaryRevenue = data?.[1]?.sparkline;
  // Locale for number/date formatting
  const locale =
    i18n.language === "sk"
      ? "sk-SK"
      : i18n.language === "cs"
      ? "cs-CZ"
      : i18n.language === "de"
      ? "de-DE"
      : "en-US";

  const formatNumber = (value, fractionDigits = 2) => {
    const num = Number(value) || 0;
    return num.toLocaleString(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };
  return (
    <>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.weekSummary.ordersSummary")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          <Stack sx={{ width: "100%", height: 240 }}>
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : !weekSummaryOrders || !weekSummaryOrders.length ? (
              <JumboListNoDataPlaceholder width={{ md: 120, xs: 180 }} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={weekSummaryOrders.map((v, i) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const offset = weekSummaryOrders.length - 1 - i;
                    const d = new Date(today);
                    d.setDate(today.getDate() - offset);
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    return { label: `${day}.${month}.`, value: v };
                  })}
                  margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                    tickFormatter={(val) =>
                      Number(val).toLocaleString(locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    }
                  />
                  <Tooltip
                    formatter={(val) => [
                      formatNumber(val, 0),
                      t("widgets.todayOrders.orders"),
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Stack>
        </JumboCardQuick>
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.weekSummary.revenueSummary")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          <Stack sx={{ width: "100%", height: 240 }}>
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : !weekSummaryRevenue || !weekSummaryRevenue.length ? (
              <JumboListNoDataPlaceholder width={{ md: 120, xs: 180 }} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={weekSummaryRevenue.map((v, i) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const offset = weekSummaryRevenue.length - 1 - i;
                    const d = new Date(today);
                    d.setDate(today.getDate() - offset);
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    return { label: `${day}.${month}.`, value: v };
                  })}
                  margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                    tickFormatter={(val) =>
                      `${Number(val).toLocaleString(locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })} ${currencyCode}`
                    }
                  />
                  <Tooltip
                    formatter={(val) => [
                      formatNumber(val, 2) + " " + currencyCode,
                      t("widgets.todayOrders.revenue"),
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Stack>
        </JumboCardQuick>
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <JumboCardQuick
          // title={
          //   <Typography variant="h2">
          //     {t("widgets.weekSummary.revenueSummary")}
          //   </Typography>
          // }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          <Stack sx={{ width: "100%" }}>
            {isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : !weekSummaryRevenue || !weekSummaryRevenue.length ? (
              <JumboListNoDataPlaceholder width={{ md: 120, xs: 180 }} />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label="week revenue by date">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("widgets.weekSummary.date")}</TableCell>
                      <TableCell align="right">
                        {t("widgets.weekSummary.revenue")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weekSummaryRevenue.map((v, i) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const offset = weekSummaryRevenue.length - 1 - i;
                      const d = new Date(today);
                      d.setDate(today.getDate() - offset);
                      const day = String(d.getDate()).padStart(2, "0");
                      const month = String(d.getMonth() + 1).padStart(2, "0");
                      const weekday = d.toLocaleDateString(locale, {
                        weekday: "short",
                      });
                      const isZero = Number(v) === 0;
                      return (
                        <TableRow
                          key={i}
                          sx={{
                            "& .MuiTableCell-root": {
                              color: isZero ? "error.main" : "inherit",
                            },
                          }}
                        >
                          <TableCell>{`${weekday} ${day}.${month}.`}</TableCell>
                          <TableCell align="right">
                            {formatNumber(v, 2) + " " + currencyCode}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow
                      sx={{
                        "& .MuiTableCell-root": { fontWeight: "bold" },
                      }}
                    >
                      <TableCell>
                        <Typography variant="h6">
                          {t("widgets.weekSummary.total")}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">
                          {formatNumber(
                            weekSummaryRevenue.reduce((acc, v) => acc + v, 0),
                            2
                          ) +
                            " " +
                            currencyCode}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </JumboCardQuick>
      </Grid>
    </>
  );
};

export default WeekSummary;
